/**
 * Notification catalogue — the institute's official circulars.
 *
 * A notification is a dated official notice (exam timetable, fee deadline,
 * counselling round, holiday list). Its payload is the attachment; it has no
 * detail page and no prose body. That is what separates it from a news post,
 * which webadmin publishes (see publishedNews.ts).
 */

import { formatBytes } from "./docUrl";

export type NotificationCategory =
  | "Examinations"
  | "Admissions"
  | "Fees"
  | "Results"
  | "General";

/** Display order for the filter rail. */
export const NOTIFICATION_CATEGORIES: NotificationCategory[] = [
  "Examinations",
  "Admissions",
  "Fees",
  "Results",
  "General",
];

/** The circular itself, as a key in the media bucket. Resolve with docUrl(). */
export interface NotificationAttachment {
  /** Media-bucket key, e.g. "sites/biet/docs/odd-sem-timetable-2026.pdf". */
  key: string;
  kind: "pdf" | "doc" | "xls";
  /** Real size of the published file, measured at ingest. */
  bytes: number;
}

export interface Notification {
  /** Stable identifier — React key and on-page anchor. Never reused. */
  id: string;
  /** ISO date, "YYYY-MM-DD". Single source of truth for display and sort. */
  date: string;
  category: NotificationCategory;
  title: string;
  /** One line of context. Rendered only on the home lead panel. */
  summary?: string;
  /** Held above all unpinned notices until manually removed. */
  pinned?: boolean;
  attachment?: NotificationAttachment;
  /** Internal route, when the notice points at a page rather than a file. */
  href?: string;
  /** Owning department, matching a DEPARTMENTS key. Omitted = institute-wide. */
  departmentCode?: string;
}

/**
 * Academic year for an issue date. The BIET year runs August to July, so
 * August–December belong to the year that just began and January–July to the
 * one that began the previous August.
 *
 * Derived rather than stored: a second field would drift from `date` the first
 * time someone edited one and not the other.
 */
export function academicYearOf(date: string): string {
  const [yStr, mStr] = date.split("-");
  const year = Number(yStr);
  const month = Number(mStr);
  const start = month >= 8 ? year : year - 1;
  const end = String((start + 1) % 100).padStart(2, "0");
  return `${start}-${end}`;
}

/** Pinned first, then date descending within each group. Never mutates input. */
export function sortNotifications(items: Notification[]): Notification[] {
  return [...items].sort((a, b) => {
    const pa = a.pinned ? 1 : 0;
    const pb = b.pinned ? 1 : 0;
    if (pa !== pb) return pb - pa;
    return b.date.localeCompare(a.date);
  });
}

/**
 * The notices the home page shows. Pinned ones lead; the rest of the slots
 * fill from recency, so the block is never empty even when nobody maintains
 * the pins.
 */
export function homeNotifications(items: Notification[], count = 4): Notification[] {
  return sortNotifications(items).slice(0, count);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * ISO date split into the pieces the register displays. Lives here rather
 * than in a component so the home block and the page cannot drift apart.
 */
export function dateParts(date: string): { day: string; month: string; year: string } {
  const [y, m, d] = date.split("-");
  return { day: d, month: MONTHS[Number(m) - 1], year: y };
}

/**
 * Accessible name for an attachment link. NotificationRow and the home lead
 * panel both render an attachment link and both need the same aria-label —
 * writing the template out twice is exactly how it drifted from the visible
 * text once already. Callers only invoke this once `item.attachment` is
 * known to exist; it falls back to the bare title otherwise.
 */
export function attachmentAriaLabel(item: Notification): string {
  if (!item.attachment) return item.title;
  return `${item.title} — ${item.attachment.kind.toUpperCase()}, ${formatBytes(item.attachment.bytes)}`;
}

/**
 * True when a notification's `href` leaves the site. Circulars routinely point
 * at an external portal — KEA, COMEDK, VTU — rather than a page here, and
 * those cannot go through react-router's <Link>, which would treat the whole
 * URL as an in-app path and fall through to the wildcard route.
 */
export function isExternalHref(href: string): boolean {
  return /^(https?:)?\/\//i.test(href) || href.startsWith("mailto:") || href.startsWith("tel:");
}

/** Distinct academic years present in the catalogue, newest first. */
export function academicYears(items: Notification[]): string[] {
  const seen = new Set(items.map((n) => academicYearOf(n.date)));
  return [...seen].sort((a, b) => b.localeCompare(a));
}

export interface RegisterPage {
  pinned: Notification[];
  visible: Notification[];
  hasMore: boolean;
}

/**
 * Split a filtered register into the pinned group and the paginated remainder.
 * Pinned notices are exempt from pagination: a run of pinned circulars must
 * never push every recent notice behind a "Load more" click.
 */
export function registerPage(filtered: Notification[], shown: number): RegisterPage {
  const pinned = filtered.filter((n) => n.pinned);
  const rest = filtered.filter((n) => !n.pinned);
  const visible = rest.slice(0, shown);
  return { pinned, visible, hasMore: visible.length < rest.length };
}

/**
 * The only way any component reads notifications.
 *
 * Today this just sorts and returns the local catalogue below. A
 * `notifications` collection is NOT part of the v1 @zivin-labs/site-content
 * contract, so there is no snapshot to prefer yet — but funneling every read
 * through this one accessor means that when the contract gains that key, the
 * change is confined to this function and no component moves.
 */
/**
 * Attachment links are suppressed site-wide for now, on request (2026-09-04) —
 * the circular PDFs behind them are not ready to publish. The attachment data
 * stays on each record below, and NotificationRow still renders one when given
 * one; it is simply stripped here, so restoring the downloads is deleting this
 * map rather than re-entering eight documents.
 */
const SHOW_ATTACHMENTS = false;

/**
 * Academic years offered in the /notifications year filter. Pinned to the
 * current year on request (2026-09-04); widen it, or swap back to
 * `academicYears(getNotifications())`, to offer earlier years again.
 */
export const FILTERABLE_ACADEMIC_YEARS = ["2026-27"];

export function getNotifications(): Notification[] {
  const items = SHOW_ATTACHMENTS
    ? NOTIFICATIONS
    : NOTIFICATIONS.map(({ attachment: _attachment, ...rest }) => rest);
  return sortNotifications(items);
}

export const NOTIFICATIONS: Notification[] = [
  {
    id: "fees-semester-payment-aug-2026",
    date: "2026-08-01",
    category: "Fees",
    title: "Last date for semester fee payment — 22 August 2026",
    summary:
      "Payment through the institute portal or by demand draft. Late payment attracts a fine of ₹500 per week.",
    pinned: true,
    attachment: { key: "sites/biet/docs/fee-circular-aug-2026.pdf", kind: "pdf", bytes: 1084 },
  },
  {
    id: "exam-timetable-odd-sem-2026",
    date: "2026-08-06",
    category: "Examinations",
    title: "Odd semester examination timetable — B.E. 3rd, 5th and 7th semesters",
    attachment: { key: "sites/biet/docs/odd-sem-timetable-2026.pdf", kind: "pdf", bytes: 1084 },
  },
  {
    id: "kcet-2026-round-2-seat-matrix",
    date: "2026-08-04",
    category: "Admissions",
    title: "KCET 2026 counselling — Round 2 seat matrix published",
    attachment: { key: "sites/biet/docs/kcet-2026-round2-matrix.pdf", kind: "pdf", bytes: 1084 },
  },
  {
    id: "revaluation-results-even-sem-2025-26",
    date: "2026-08-02",
    category: "Results",
    title: "Revaluation results — even semester 2025-26",
    attachment: { key: "sites/biet/docs/revaluation-even-2025-26.pdf", kind: "pdf", bytes: 1084 },
  },
  {
    id: "revised-holiday-list-aug-2026",
    date: "2026-07-29",
    category: "General",
    title: "Revised holiday list — August 2026",
    attachment: { key: "sites/biet/docs/holiday-list-aug-2026.pdf", kind: "pdf", bytes: 1084 },
  },
  {
    // An external portal rather than a page here or a PDF — the third payload
    // shape a circular can carry.
    id: "kea-ug-counselling-portal-2026",
    date: "2026-07-22",
    category: "Admissions",
    title: "KEA UG counselling — candidate login and document verification portal",
    href: "https://cetonline.karnataka.gov.in/kea/",
  },
  {
    id: "exam-fee-notification-even-2025-26",
    date: "2026-02-10",
    category: "Examinations",
    title: "Examination fee notification — even semester 2025-26",
    attachment: { key: "sites/biet/docs/exam-fee-even-2025-26.pdf", kind: "pdf", bytes: 1084 },
  },
];
